// src/pages/finance-portal/benefit-plans/BenefitPlans.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, Tabs, Tab,
    InputAdornment, IconButton, Tooltip, MenuItem, Grid,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Autocomplete, CircularProgress, Switch, FormControlLabel,
} from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const BENEFIT_TYPES = [
    { value: 'HEALTH_INSURANCE', label: 'Health Insurance' },
    { value: 'DENTAL_INSURANCE', label: 'Dental Insurance' },
    { value: 'VISION_INSURANCE', label: 'Vision Insurance' },
    { value: 'LIFE_INSURANCE', label: 'Life Insurance' },
    { value: '401K', label: '401(k)' },
    { value: 'HSA', label: 'HSA' },
    { value: 'FSA', label: 'FSA' },
    { value: 'OTHER', label: 'Other' },
];

const DEDUCTION_TIMINGS = [
    { value: 'PRE_TAX', label: 'Pre-Tax' },
    { value: 'POST_TAX', label: 'Post-Tax' },
];

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getTypeLabel(type) {
    const found = BENEFIT_TYPES.find(b => b.value === type);
    return found ? found.label : (type || '—');
}

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

export default function BenefitPlans() {
    const [activeTab, setActiveTab] = useState(0); // 0=Plans, 1=Enrollments

    // ---- Plans State ----
    const [plans, setPlans] = useState([]);
    const [plansTotal, setPlansTotal] = useState(0);
    const [plansPage, setPlansPage] = useState(0);
    const [plansPerPage, setPlansPerPage] = useState(20);
    const [plansLoading, setPlansLoading] = useState(true);
    const [planSearch, setPlanSearch] = useState('');

    // Plan Form Dialog
    const [planFormOpen, setPlanFormOpen] = useState(false);
    const [planEditData, setPlanEditData] = useState(null);
    const [planForm, setPlanForm] = useState({
        plan_name: '', benefit_type: 'HEALTH_INSURANCE', deduction_timing: 'PRE_TAX',
        employee_amount: '', employer_amount: '', employer_match_percent: '',
        employer_match_limit_percent: '', annual_limit: '', description: '',
    });
    const [planSaving, setPlanSaving] = useState(false);

    // ---- Enrollments State ----
    const [enrollments, setEnrollments] = useState([]);
    const [enrollTotal, setEnrollTotal] = useState(0);
    const [enrollPage, setEnrollPage] = useState(0);
    const [enrollPerPage, setEnrollPerPage] = useState(20);
    const [enrollLoading, setEnrollLoading] = useState(true);

    // Enrollment Form Dialog
    const [enrollFormOpen, setEnrollFormOpen] = useState(false);
    const [enrollForm, setEnrollForm] = useState({
        employee_id: '', plan_id: '', employee_amount: '', employer_amount: '', effective_date: '',
    });
    const [enrollSaving, setEnrollSaving] = useState(false);

    // Shared
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState(null);
    const [deleteType, setDeleteType] = useState('plan'); // plan or enrollment
    const [deleting, setDeleting] = useState(false);

    // Employee autocomplete
    const [employees, setEmployees] = useState([]);
    const [empSearch, setEmpSearch] = useState('');
    const [empLoading, setEmpLoading] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const empTimer = useRef(null);

    // Plan options for enrollment
    const [planOptions, setPlanOptions] = useState([]);

    // ---- Fetch Plans ----
    const fetchPlans = useCallback(async () => {
        setPlansLoading(true);
        try {
            const params = { page: plansPage + 1, per_page: plansPerPage };
            if (planSearch) params.search = planSearch;
            const res = await axiosInstance.get('/api/finance/payroll/benefit-plans', { params });
            if (res.data.success) {
                setPlans(res.data.data || []);
                setPlansTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load benefit plans');
        } finally {
            setPlansLoading(false);
        }
    }, [plansPage, plansPerPage, planSearch]);

    // ---- Fetch Enrollments ----
    const fetchEnrollments = useCallback(async () => {
        setEnrollLoading(true);
        try {
            const params = { page: enrollPage + 1, per_page: enrollPerPage };
            const res = await axiosInstance.get('/api/finance/payroll/benefit-enrollments', { params });
            if (res.data.success) {
                setEnrollments(res.data.data || []);
                setEnrollTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load enrollments');
        } finally {
            setEnrollLoading(false);
        }
    }, [enrollPage, enrollPerPage]);

    useEffect(() => { fetchPlans(); }, [fetchPlans]);
    useEffect(() => { if (activeTab === 1) fetchEnrollments(); }, [activeTab, fetchEnrollments]);

    // Fetch plan options for enrollment form
    useEffect(() => {
        const loadPlanOptions = async () => {
            try {
                const res = await axiosInstance.get('/api/finance/payroll/benefit-plans', { params: { per_page: 200 } });
                if (res.data.success) setPlanOptions(res.data.data || []);
            } catch (err) { /* ignore */ }
        };
        loadPlanOptions();
    }, []);

    // Employee search
    useEffect(() => {
        if (empTimer.current) clearTimeout(empTimer.current);
        setEmpLoading(true);
        empTimer.current = setTimeout(async () => {
            try {
                const params = { per_page: 50 };
                if (empSearch && empSearch.length >= 2) params.search = empSearch;
                const res = await axiosInstance.get('/api/finance/payroll/employees', { params });
                if (res.data.success) setEmployees(res.data.data || []);
            } catch (err) { /* ignore */ }
            finally { setEmpLoading(false); }
        }, empSearch ? 300 : 0);
        return () => { if (empTimer.current) clearTimeout(empTimer.current); };
    }, [empSearch]);

    // ---- Plan CRUD ----
    const openPlanForm = (plan) => {
        if (plan) {
            setPlanEditData(plan);
            setPlanForm({
                plan_name: plan.plan_name || '',
                benefit_type: plan.benefit_type || 'HEALTH_INSURANCE',
                deduction_timing: plan.deduction_timing || 'PRE_TAX',
                employee_amount: plan.employee_amount || '',
                employer_amount: plan.employer_amount || '',
                employer_match_percent: plan.employer_match_percent || '',
                employer_match_limit_percent: plan.employer_match_limit_percent || '',
                annual_limit: plan.annual_limit || '',
                description: plan.description || '',
            });
        } else {
            setPlanEditData(null);
            setPlanForm({
                plan_name: '', benefit_type: 'HEALTH_INSURANCE', deduction_timing: 'PRE_TAX',
                employee_amount: '', employer_amount: '', employer_match_percent: '',
                employer_match_limit_percent: '', annual_limit: '', description: '',
            });
        }
        setPlanFormOpen(true);
    };

    const handleSavePlan = async () => {
        setPlanSaving(true);
        setError(null);
        try {
            const payload = {
                ...planForm,
                employee_amount: parseFloat(planForm.employee_amount) || 0,
                employer_amount: parseFloat(planForm.employer_amount) || 0,
                employer_match_percent: parseFloat(planForm.employer_match_percent) || 0,
                employer_match_limit_percent: parseFloat(planForm.employer_match_limit_percent) || 0,
                annual_limit: parseFloat(planForm.annual_limit) || null,
            };
            if (planEditData) {
                await axiosInstance.put(`/api/finance/payroll/benefit-plans/${planEditData.id}`, payload);
            } else {
                await axiosInstance.post('/api/finance/payroll/benefit-plans', payload);
            }
            setPlanFormOpen(false);
            setSuccess('Plan saved');
            fetchPlans();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save plan');
        } finally {
            setPlanSaving(false);
        }
    };

    // ---- Enrollment CRUD ----
    const openEnrollForm = () => {
        setEnrollForm({ employee_id: '', plan_id: '', employee_amount: '', employer_amount: '', effective_date: new Date().toISOString().split('T')[0] });
        setSelectedEmployee(null);
        setEnrollFormOpen(true);
    };

    const handleSaveEnrollment = async () => {
        setEnrollSaving(true);
        setError(null);
        try {
            const payload = {
                ...enrollForm,
                employee_amount: enrollForm.employee_amount ? parseFloat(enrollForm.employee_amount) : null,
                employer_amount: enrollForm.employer_amount ? parseFloat(enrollForm.employer_amount) : null,
            };
            await axiosInstance.post('/api/finance/payroll/benefit-enrollments', payload);
            setEnrollFormOpen(false);
            setSuccess('Employee enrolled');
            fetchEnrollments();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to enroll employee');
        } finally {
            setEnrollSaving(false);
        }
    };

    const handleCancelEnrollment = async (id) => {
        try {
            await axiosInstance.post(`/api/finance/payroll/benefit-enrollments/${id}/cancel`);
            setSuccess('Enrollment cancelled');
            fetchEnrollments();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to cancel enrollment');
        }
    };

    // ---- Delete ----
    const handleDelete = async () => {
        if (!deleteDialog) return;
        setDeleting(true);
        try {
            const endpoint = deleteType === 'plan'
                ? `/api/finance/payroll/benefit-plans/${deleteDialog}`
                : `/api/finance/payroll/benefit-enrollments/${deleteDialog}`;
            await axiosInstance.delete(endpoint);
            setDeleteDialog(null);
            setSuccess(`${deleteType === 'plan' ? 'Plan' : 'Enrollment'} deleted`);
            if (deleteType === 'plan') fetchPlans(); else fetchEnrollments();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <HealthAndSafetyIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Benefit Plans</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Manage benefit plans &amp; enrollments</Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => activeTab === 0 ? openPlanForm(null) : openEnrollForm()}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'white', color: ACCENT,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                        }}
                    >
                        {activeTab === 0 ? 'Add Plan' : 'Enroll Employee'}
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Plans / Enrollments Tabs */}
                <Tabs
                    value={activeTab}
                    onChange={(_, val) => setActiveTab(val)}
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': { fontFamily, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', color: '#64748b', minHeight: 40 },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                    }}
                >
                    <Tab label="Plans" />
                    <Tab label="Enrollments" />
                </Tabs>

                {/* ===================== PLANS TAB ===================== */}
                {activeTab === 0 && (
                    <>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                            <TextField
                                size="small" placeholder="Search plans..."
                                value={planSearch}
                                onChange={e => { setPlanSearch(e.target.value); setPlansPage(0); }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                                    sx: { fontFamily, borderRadius: '10px' },
                                }}
                                sx={{ minWidth: { xs: '100%', sm: 280 } }}
                            />
                        </Box>

                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <TableContainer sx={{ overflowX: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                            {['Plan Name', 'Type', 'Timing', 'Employee Amt', 'Employer Amt', 'Match %', 'Status', 'Actions'].map(h => (
                                                <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {plansLoading ? (
                                            [1, 2, 3].map(i => (
                                                <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7, 8].map(j => <TableCell key={j}><Skeleton height={36} /></TableCell>)}</TableRow>
                                            ))
                                        ) : plans.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                                    <Typography sx={{ fontFamily, color: '#94a3b8' }}>No benefit plans found</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : plans.map(p => (
                                            <TableRow key={p.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{p.plan_name}</TableCell>
                                                <TableCell sx={bodyCellSx}>{getTypeLabel(p.benefit_type)}</TableCell>
                                                <TableCell sx={bodyCellSx}>
                                                    <Chip label={p.deduction_timing === 'PRE_TAX' ? 'Pre-Tax' : 'Post-Tax'} size="small"
                                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.72rem', bgcolor: p.deduction_timing === 'PRE_TAX' ? '#dbeafe' : '#fef3c7', color: p.deduction_timing === 'PRE_TAX' ? '#1e40af' : '#92400e' }} />
                                                </TableCell>
                                                <TableCell sx={bodyCellSx}>{formatCurrency(p.employee_amount)}</TableCell>
                                                <TableCell sx={bodyCellSx}>{formatCurrency(p.employer_amount)}</TableCell>
                                                <TableCell sx={bodyCellSx}>{p.employer_match_percent ? `${p.employer_match_percent}%` : '—'}</TableCell>
                                                <TableCell sx={bodyCellSx}>
                                                    <Chip label={p.is_active !== false ? 'Active' : 'Inactive'} size="small"
                                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.72rem', bgcolor: p.is_active !== false ? '#dcfce7' : '#f1f5f9', color: p.is_active !== false ? '#166534' : '#64748b' }} />
                                                </TableCell>
                                                <TableCell sx={bodyCellSx}>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Tooltip title="Edit">
                                                            <IconButton size="small" onClick={() => openPlanForm(p)}>
                                                                <EditIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" onClick={() => { setDeleteDialog(p.id); setDeleteType('plan'); }}>
                                                                <DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div" count={plansTotal} page={plansPage}
                                onPageChange={(_, p) => setPlansPage(p)}
                                rowsPerPage={plansPerPage}
                                onRowsPerPageChange={e => { setPlansPerPage(parseInt(e.target.value, 10)); setPlansPage(0); }}
                                rowsPerPageOptions={[10, 20, 50]}
                                sx={{ '& *': { fontFamily } }}
                            />
                        </Card>
                    </>
                )}

                {/* ===================== ENROLLMENTS TAB ===================== */}
                {activeTab === 1 && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {['Employee', 'Plan', 'Employee Amt', 'Employer Amt', 'Effective Date', 'End Date', 'Active', 'Actions'].map(h => (
                                            <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {enrollLoading ? (
                                        [1, 2, 3].map(i => (
                                            <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7, 8].map(j => <TableCell key={j}><Skeleton height={36} /></TableCell>)}</TableRow>
                                        ))
                                    ) : enrollments.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8' }}>No enrollments found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : enrollments.map(e => (
                                        <TableRow key={e.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{e.employee_name || '—'}</TableCell>
                                            <TableCell sx={bodyCellSx}>{e.plan_name || '—'}</TableCell>
                                            <TableCell sx={bodyCellSx}>{e.employee_amount != null ? formatCurrency(e.employee_amount) : '—'}</TableCell>
                                            <TableCell sx={bodyCellSx}>{e.employer_amount != null ? formatCurrency(e.employer_amount) : '—'}</TableCell>
                                            <TableCell sx={bodyCellSx}>{formatDate(e.effective_date)}</TableCell>
                                            <TableCell sx={bodyCellSx}>{formatDate(e.end_date)}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip label={e.is_active ? 'Active' : 'Inactive'} size="small"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.72rem', bgcolor: e.is_active ? '#dcfce7' : '#f1f5f9', color: e.is_active ? '#166534' : '#64748b' }} />
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                {e.is_active && (
                                                    <Tooltip title="Cancel Enrollment">
                                                        <IconButton size="small" onClick={() => handleCancelEnrollment(e.id)}>
                                                            <CancelIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div" count={enrollTotal} page={enrollPage}
                            onPageChange={(_, p) => setEnrollPage(p)}
                            rowsPerPage={enrollPerPage}
                            onRowsPerPageChange={e => { setEnrollPerPage(parseInt(e.target.value, 10)); setEnrollPage(0); }}
                            rowsPerPageOptions={[10, 20, 50]}
                            sx={{ '& *': { fontFamily } }}
                        />
                    </Card>
                )}
            </Box>

            {/* ===================== PLAN FORM DIALOG ===================== */}
            <Dialog open={planFormOpen} onClose={() => setPlanFormOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>{planEditData ? 'Edit Benefit Plan' : 'Add Benefit Plan'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Plan Name" required
                                value={planForm.plan_name} onChange={e => setPlanForm(prev => ({ ...prev, plan_name: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth select label="Benefit Type"
                                value={planForm.benefit_type} onChange={e => setPlanForm(prev => ({ ...prev, benefit_type: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }}>
                                {BENEFIT_TYPES.map(t => <MenuItem key={t.value} value={t.value} sx={{ fontFamily }}>{t.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth select label="Deduction Timing"
                                value={planForm.deduction_timing} onChange={e => setPlanForm(prev => ({ ...prev, deduction_timing: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }}>
                                {DEDUCTION_TIMINGS.map(t => <MenuItem key={t.value} value={t.value} sx={{ fontFamily }}>{t.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Employee Amount" type="number"
                                value={planForm.employee_amount} onChange={e => setPlanForm(prev => ({ ...prev, employee_amount: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Employer Amount" type="number"
                                value={planForm.employer_amount} onChange={e => setPlanForm(prev => ({ ...prev, employer_amount: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField size="small" fullWidth label="Match %" type="number"
                                value={planForm.employer_match_percent} onChange={e => setPlanForm(prev => ({ ...prev, employer_match_percent: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField size="small" fullWidth label="Match Limit %" type="number"
                                value={planForm.employer_match_limit_percent} onChange={e => setPlanForm(prev => ({ ...prev, employer_match_limit_percent: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField size="small" fullWidth label="Annual Limit" type="number"
                                value={planForm.annual_limit} onChange={e => setPlanForm(prev => ({ ...prev, annual_limit: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Description" multiline rows={2}
                                value={planForm.description} onChange={e => setPlanForm(prev => ({ ...prev, description: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setPlanFormOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleSavePlan} variant="contained" disabled={planSaving || !planForm.plan_name}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {planSaving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : (planEditData ? 'Update' : 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ===================== ENROLLMENT FORM DIALOG ===================== */}
            <Dialog open={enrollFormOpen} onClose={() => setEnrollFormOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Enroll Employee</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <Autocomplete
                                size="small" options={employees}
                                getOptionLabel={opt => opt ? `${opt.first_name || ''} ${opt.last_name || ''}`.trim() || opt.email || '' : ''}
                                value={selectedEmployee}
                                onChange={(_, val) => { setSelectedEmployee(val); setEnrollForm(prev => ({ ...prev, employee_id: val ? val.id : '' })); }}
                                onInputChange={(_, val) => setEmpSearch(val)}
                                loading={empLoading}
                                renderInput={(params) => (
                                    <TextField {...params} label="Employee" required
                                        InputProps={{ ...params.InputProps, sx: { fontFamily, borderRadius: '10px' },
                                            endAdornment: <>{empLoading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</> }}
                                        InputLabelProps={{ sx: { fontFamily } }} />
                                )}
                                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                noOptionsText="Type to search employees"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth select label="Plan" required
                                value={enrollForm.plan_id} onChange={e => setEnrollForm(prev => ({ ...prev, plan_id: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }}>
                                {planOptions.map(p => <MenuItem key={p.id} value={p.id} sx={{ fontFamily }}>{p.plan_name} ({getTypeLabel(p.benefit_type)})</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Employee Amount Override" type="number"
                                value={enrollForm.employee_amount} onChange={e => setEnrollForm(prev => ({ ...prev, employee_amount: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }}
                                helperText="Leave blank to use plan default" />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Employer Amount Override" type="number"
                                value={enrollForm.employer_amount} onChange={e => setEnrollForm(prev => ({ ...prev, employer_amount: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }}
                                helperText="Leave blank to use plan default" />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Effective Date" type="date" required
                                value={enrollForm.effective_date} onChange={e => setEnrollForm(prev => ({ ...prev, effective_date: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEnrollFormOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleSaveEnrollment} variant="contained" disabled={enrollSaving || !enrollForm.employee_id || !enrollForm.plan_id}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {enrollSaving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Enroll'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete {deleteType === 'plan' ? 'Plan' : 'Enrollment'}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialog(null)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleDelete} variant="contained" disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}>
                        {deleting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
